import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

export default function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoice");
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      if (!invoiceId) return;

      const { data: invoiceData, error: invoiceError } = await supabase
        .from("invoices")
        .select("*")
        .eq("id", invoiceId)
        .single();

      if (invoiceError) throw invoiceError;
      setInvoice(invoiceData);

      if (invoiceData.user_id) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", invoiceData.user_id)
          .single();
        setCustomer(profileData);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-500">
        Loading confirmation...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center mt-20 text-red-600 font-medium">
        Invoice not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-5xl mx-auto">

        {/* Success Banner */}
        <div className="bg-green-600 text-white rounded-t-xl p-6 text-center">
          <h1 className="text-3xl font-bold">
            Payment Successful
          </h1>
          <p className="mt-2 opacity-90">
            Your payment has been received successfully.
          </p>
        </div>

        {/* Invoice Card */}
        <div className="bg-white shadow-xl rounded-b-xl p-10">

          {/* Header */}
          <div className="flex justify-between items-start mb-16">

            <div>
              <h2 className="text-2xl font-bold text-[#13375c]">
                NRI Local Buddy
              </h2>

              <p className="text-gray-600 mt-2">
                Hyderabad, India
              </p>

              <p className="text-gray-600">
                support@nrilocalbuddy.com
              </p>
            </div>

            <div className="text-right">
              <h1 className="text-5xl font-bold tracking-[10px]">
                INVOICE
              </h1>

              <span className="inline-block mt-4 px-4 py-2 bg-green-100 text-green-700 rounded-full font-bold">
                PAID
              </span>
            </div>
          </div>

          {/* Customer + Invoice Details */}
          <div className="grid md:grid-cols-2 gap-10 mb-12">

            <div>
              <h3 className="font-bold text-lg mb-3">
                Bill To
              </h3>

              <p className="font-semibold">
                {customer?.full_name || "Customer"}
              </p>

              <p>
                {customer?.email || invoice.user_email}
              </p>

              <p>
                {customer?.phone_number || "-"}
              </p>
            </div>

            <div className="text-right">

              <div className="space-y-3">

                <div className="flex justify-between">
                  <span className="font-semibold">
                    Invoice #
                  </span>

                  <span>
                    {invoice.invoice_number}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="font-semibold">
                    Invoice Date
                  </span>

                  <span>
                    {new Date(
                      invoice.created_at
                    ).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="font-semibold">
                    Status
                  </span>

                  <span className="text-green-600 font-bold uppercase">
                    {invoice.status}
                  </span>
                </div>

              </div>

            </div>

          </div>

          {/* Invoice Table */}
          <div className="border border-gray-300">

            <table className="w-full">

              <thead>

                <tr className="bg-gray-100">

                  <th className="text-left p-4">
                    Item
                  </th>

                  <th className="text-left p-4">
                    Description
                  </th>

                  <th className="text-right p-4">
                    Amount
                  </th>

                </tr>

              </thead>

              <tbody>

                <tr>

                  <td className="p-4 border-t">
                    Service
                  </td>

                  <td className="p-4 border-t">
                    {invoice.title}
                    <div className="text-sm text-gray-500 mt-1">
                      {invoice.category}
                    </div>
                  </td>

                  <td className="p-4 border-t text-right font-semibold">
                    {invoice.currency}
                    {" "}
                    {Number(invoice.amount).toFixed(2)}
                  </td>

                </tr>

              </tbody>

            </table>

            {/* Notes */}
            <div className="p-6 border-t min-h-[120px]">

              <h4 className="font-bold underline mb-2">
                NOTES
              </h4>

              <p className="text-gray-700">
                {invoice.description ||
                  "Payment received successfully for requested service."}
              </p>

            </div>

          </div>

          {/* Totals */}
          <div className="flex justify-end mt-8">

            <div className="w-full md:w-96 border">

              <div className="flex justify-between p-4 border-b">
                <span>
                  Subtotal
                </span>

                <span>
                  {invoice.currency}
                  {" "}
                  {Number(invoice.amount).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between p-4 border-b">
                <span>
                  Amount Paid
                </span>

                <span className="text-green-600 font-bold">
                  {invoice.currency}
                  {" "}
                  {Number(invoice.amount).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between p-4 bg-gray-50 font-bold text-xl">
                <span>
                  Balance Due
                </span>

                <span className="text-green-600">
                  {invoice.currency} 0.00
                </span>
              </div>

            </div>

          </div>

          {/* Footer Buttons */}

          <div className="mt-10 flex gap-4 justify-center">

            <button
              onClick={() => window.print()}
              className="px-6 py-3 bg-[#13375c] text-white rounded-lg font-semibold"
            >
              Download Invoice
            </button>

            <Link
              to="/dashboard"
              className="px-6 py-3 bg-gray-800 text-white rounded-lg font-semibold"
            >
              Dashboard
            </Link>

          </div>

        </div>
      </div>
    </div>
  );
}